/** One KPI tile on the hero cover slide. */
export interface HeroKpiTile {
  label: string;
  value: string;
  sub?: string;
}

/** Canonical hero KPI label for members engaged this month. */
export const HERO_KPI_LABEL_MEMBERS_ENGAGED = 'Members Engaged';

/** Canonical hero KPI label for eligible engagement rate. */
export const HERO_KPI_LABEL_ENGAGEMENT_RATE = 'Engagement Rate';

/** Canonical hero KPI label for needs fulfilled rate. */
export const HERO_KPI_LABEL_NEEDS_FULFILLED = 'Needs Fulfilled';

/** Canonical hero KPI label for member satisfaction. */
export const HERO_KPI_LABEL_SATISFACTION_SCORE = 'Satisfaction Score';

const normalizeHeroKpiLabel = (label: string): string =>
  label.trim().toLowerCase();

/**
 * Studio subcopy shown under each hero KPI value.
 * @param label - KPI tile label from snapshot data.
 * @param customerName - Report customer display name.
 * @returns Subtitle line under the metric value.
 */
export const buildHeroKpiSubcopy = (
  label: string,
  customerName: string,
): string => {
  const key = normalizeHeroKpiLabel(label);
  if (key.includes('members engaged') || key === 'engaged members') {
    return `Across ${customerName} this month`;
  }
  if (key.includes('engagement rate')) {
    return 'Of members we reached out to';
  }
  if (key.includes('needs fulfilled')) {
    return 'Identified needs resolved';
  }
  if (key.includes('satisfaction')) {
    return 'Average member rating';
  }
  return '';
};

/**
 * Resolves the subtitle for a hero KPI tile (snapshot override or default copy).
 * @param tile - KPI tile from snapshot JSON.
 * @param customerName - Report customer display name.
 * @returns Subtitle shown below the animated value.
 */
export const resolveHeroKpiSubcopy = (
  tile: HeroKpiTile,
  customerName: string,
): string =>
  typeof tile.sub === 'string' && tile.sub.length > 0
    ? tile.sub
    : buildHeroKpiSubcopy(tile.label, customerName);
